import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Calendar,
  LayoutDashboard,
  LogOut,
  Settings,
  Plus,
  Search,
  User as UserIcon,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

interface Meeting {
  meeting_id: number;
  title: string;
  meeting_date: string;
  location: string;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CommandPalette = ({ open, onOpenChange }: CommandPaletteProps) => {
  const [search, setSearch] = useState('');
  const [recentMeetings, setRecentMeetings] = useState<Meeting[]>([]);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';

  // Cmd/Ctrl + K to open - now controls parent state
  useHotkeys('mod+k', (e) => {
    e.preventDefault();
    onOpenChange(!open);
  });

  // Additional shortcuts when palette is closed
  useHotkeys('mod+n', () => navigate('/new-meeting'), { enabled: !open });
  useHotkeys('mod+h', () => navigate('/history'), { enabled: !open });
  useHotkeys('mod+d', () => navigate('/dashboard'), { enabled: !open });

  // Fetch recent meetings for search
  useEffect(() => {
    if (open) {
      // Fetch from API or use existing store
      // For now, we'll use empty array - integrate with your API
      setRecentMeetings([]);
    }
  }, [open]);

  const handleSelect = useCallback(
    (callback: () => void) => {
      onOpenChange(false);
      callback();
    },
    [onOpenChange]
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Wyszukaj akcje, spotkania, notatki..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>Nie znaleziono wyników dla "{search}"</CommandEmpty>

        {/* Quick Actions */}
        <CommandGroup heading="Szybkie akcje">
          <CommandItem
            onSelect={() => handleSelect(() => navigate('/new-meeting'))}
            className="cursor-pointer"
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>Nowe spotkanie</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <span className="text-xs">⌘</span>N
            </kbd>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate('/history'))}
            className="cursor-pointer"
          >
            <Search className="mr-2 h-4 w-4" />
            <span>Szukaj w historii</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <span className="text-xs">⌘</span>H
            </kbd>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Navigation */}
        <CommandGroup heading="Nawigacja">
          <CommandItem
            onSelect={() => handleSelect(() => navigate('/dashboard'))}
            className="cursor-pointer"
          >
            <LayoutDashboard className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <span className="text-xs">⌘</span>D
            </kbd>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate('/history'))}
            className="cursor-pointer"
          >
            <Calendar className="mr-2 h-4 w-4" />
            <span>Historia spotkań</span>
          </CommandItem>
          {isAdmin && (
            <CommandItem
              onSelect={() => handleSelect(() => navigate('/admin'))}
              className="cursor-pointer"
            >
              <Settings className="mr-2 h-4 w-4" />
              <span>Admin Panel</span>
            </CommandItem>
          )}
        </CommandGroup>

        {/* Recent Meetings - if any */}
        {recentMeetings.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Ostatnie spotkania">
              {recentMeetings.map((meeting) => (
                <CommandItem
                  key={meeting.meeting_id}
                  onSelect={() =>
                    handleSelect(() => navigate(`/meeting/${meeting.meeting_id}`))
                  }
                  className="cursor-pointer"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>{meeting.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(meeting.meeting_date), 'dd MMM yyyy', {
                        locale: pl,
                      })}{' '}
                      • {meeting.location}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />

        {/* User Actions */}
        <CommandGroup heading="Konto">
          <CommandItem disabled className="text-muted-foreground">
            <UserIcon className="mr-2 h-4 w-4" />
            <span>
              {user?.fullName} ({user?.email})
            </span>
          </CommandItem>
          <CommandItem
            onSelect={() =>
              handleSelect(() => {
                logout();
                navigate('/login');
              })
            }
            className="cursor-pointer text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Wyloguj się</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
